/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, HStack, IconButton, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import {
  PrimaryButton,
  CQTreeTable,
  usePaginationSessionStorage
} from '@laxmimanogna/code-quick-components';

import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { MyChartContext } from '../../../../providers/MyChartProvider';
import AppColors from '../../../../constants/AppColors';
import { EyeAvatarIcon, UploadIcon } from '../../../../constants/IconData';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../../constants/Routes';
import { replaceRoute } from '../../../../utils/common.utils';

import {
  CHART_SUBHEADER,
  ChartStatusBadge,
  BatchStatusIndicator,
  AttachmentCell,
  CompletedDateCell,
  getBatchStatus,
} from './table_config';
import { createTreeTableProps, paginationProps } from '../../../../utils/cq_tree_table_props';
import AuditSheetFileViewer from '../../../audit_sheet/components/AuditSheetFileViewer';
import NotesDrawer from '../../../audit_sheet/components/NotesDrawer';
import { AuditSheetContext } from '../../../../providers/AuditSheetProvider';
import AddAdditionalDocsModal from '../AddAdditionalDocsModal';
import { AuthContext } from '../../../../providers/AuthProvider';
import { ClientType } from '../../../../utils/clientTypeUtils';

const columnHelper = createColumnHelper();
const ATTACHABLE_CHART_STATUSES = ['queued', 'on_hold', 'in_review'];

const NewChartsTable = ({
    onOpenNotes,
    PAGE_NAME,
    TABLE_NAME,
    filterResetTrigger,
    tableToggleData
}) => {
  const {
    getPaginationSession,
    setPaginationSession
  } = usePaginationSessionStorage();
  const myChartContext = useContext(MyChartContext);
  const authContext = useContext(AuthContext)
  const [ordering, setOrdering] = useState(getPaginationSession(PAGE_NAME, TABLE_NAME)?.ordering || '');
  const { 
    allCharts, 
    isAllChartLoading, 
    getAllCharts, 
    filterParams,
    handleApplyFilter 
  } =
    myChartContext;
  const navigate = useNavigate();
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [selectedChart, setSelectedChart] = useState(null);
  const addDocsModal = useDisclosure();
  const tableRef = React.useRef(null);

  useEffect(() => {
    if (tableToggleData.expand) {
      (allCharts ?? []).forEach((_, idx) => tableRef.current.expandRow(String(idx)))
    }
  }, [tableToggleData.expand]);

  useEffect(() => {
    if (tableToggleData.collapse) {
      tableRef.current.collapseAll();
    }
  }, [tableToggleData.collapse])

  useEffect(() => {
    if (filterResetTrigger === null) {
      return;
    }
    setOrdering(null);
    tableRef.current?.setCustomSorting();
  }, [filterResetTrigger])

  useEffect(() => {
    if (!ordering) {
      return;
    }
    let rev = false, key = ordering;
    if (ordering[0] === "-") {
      rev = true;
      key = ordering.substring(1);
    }

    tableRef?.current.setCustomSorting([{'id': key, desc: rev}])
  }, [])

  // keeps the batch data in session storage so that we can 
  // highlight this item from the table after returning to this page
  const onBatchNavigation = (
    batchRowId,
    batchId
  ) => {
    getAllCharts.saveNavigationState(
      PAGE_NAME,
      TABLE_NAME,
      [batchRowId, batchId]
    );
  }

  // keeps the chart data in session storage so that we can 
  // highlight this item from the table after returning to this page
  const onChartNavigation = (
    batchRowId,
    chartRowId,
    batchId,
    chartId
  ) => {
    getAllCharts.saveNavigationState(
      PAGE_NAME,
      TABLE_NAME,
      [
        batchRowId,
        chartRowId, 
        batchId,
        chartId
      ]
    );
  }

  // SCROLL CHANGE: Auto-restore
  const initializingNavState = () => {
    if ((allCharts ?? []).length > 0) {
      const itemId = getAllCharts.restoreNavigationState(PAGE_NAME, TABLE_NAME, true);
      // tableRef.current.collapseAll();
      if (itemId) {
        const navData = itemId.split(',').map(Number);

        // for viewing batch 
        if (navData.length === 2) {
          const [batchRow, batchId] = navData;
          if (allCharts?.[batchRow]?.upload_id == batchId) {
            setHighlightedRowId(batchId);
          } 
        } else {
          // for viewing chart 
          const [batchRow, chartRow , batchId, chartId] = navData;
          if (allCharts?.[batchRow]?.upload_id == batchId && allCharts?.[batchRow]?.subRows?.[chartRow].id === chartId) {
            tableRef.current.expandRow(String(batchRow));
            setHighlightedRowId(chartId);
          }
        }             
                          
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 2000);
      }
    }
  }

  useEffect(() => {
    initializingNavState();
  }, [myChartContext.allCharts]);


  useEffect(() => {
    let searching = !!(filterParams.searchText && filterParams.searchText.trim());
    getAllCharts.loadAPI(PAGE_NAME, TABLE_NAME, searching);
  }, [filterParams.ordering, filterParams.chartStatusType, filterParams.triggerSearch, getAllCharts.showOrdering]);

  useEffect(() => {
    if ((ordering || filterParams.ordering) && ordering !== filterParams.ordering) {
      setPaginationSession(PAGE_NAME, TABLE_NAME, {
        ...getPaginationSession(PAGE_NAME, TABLE_NAME),
        page: 1,
        size: 10
      })
      handleApplyFilter({
        ...filterParams,
        ordering: ordering
      })
    }
  }, [ordering]);

  const handleAddAdditionalDocs = chart => {
    setSelectedChart(chart);
    addDocsModal.onOpen();
  };

  const columns = useMemo(() => [

    // ── Col 1: Batch / Chart ID ───────────────────────────────────────────────
    // subHeader value is an object → enables custom bg/color AND sortable toggle
    columnHelper.accessor('name', {
      id: 'name',
      header: () => 'Batch',
      subHeader: {
        1: { label: 'File', ...CHART_SUBHEADER, sortable: true },
      },
      cell: info => {
        const isBatch = info.row.depth === 0;
        const chartData = info.row.original;
        const cellValue = info.getValue();
        const isReceived = chartData.status === 'completed';

        const isTruncated = cellValue?.length > 10;
        const truncatedValue = isTruncated
          ? `${cellValue.slice(0, 10)}...`
          : cellValue;

        if (isBatch) {
          return (
            <Tooltip
              hasArrow
              backgroundColor={!isReceived ? AppColors.secondary : undefined}
              placement="bottom"
              label={
                isReceived
                  ? (isTruncated ? cellValue : '') // show only if truncated
                  : (
                      <>
                        Batch pending review.
                        {isTruncated && (
                          <>
                            <br />
                            {cellValue}
                          </>
                        )}
                      </>
                    )
              }
            >
              <Text
                whiteSpace="nowrap"
                _hover={isReceived ? { textDecoration: 'underline' } : {}}
                cursor={'pointer'}
                onClick={() => {
                  if (!isReceived) return;

                  const batchDashboard = replaceRoute(ROUTES.BATCH_DASHBOARD, {
                    batchId: chartData.upload_id,
                  });
                  navigate(batchDashboard, {
                    state: { chartData: chartData },
                  });
                }}
                fontWeight={600}
                color="blue.800"
                fontSize="14px"
                textAlign="start"
              >
                {truncatedValue}
              </Text>
            </Tooltip>
          );
        }

        return (
          <Text
            fontWeight={500}
            textAlign="start"
            color="blue.700"
            fontSize="13px"
          >
            {cellValue}
          </Text>
        );
      },
    }),

    // ── Col 2: Upload Date (batch) / Attachments (chart) ──────────────────────
    columnHelper.accessor('upload_date', {
      id: 'upload_date',
      header: () => 'Upload Date',
      subHeader: {
        1: { label: 'Attachments', ...CHART_SUBHEADER },
        // sortable omitted → no sort icon on this sub-header column
      },
      cell: info => {
        const isBatch = info.row.depth === 0;
        const attachmentNames = [
          info.row.original.file_obj?.name,
          info.row.original.file_obj?.audited_chart_name,
          ...(info.row.original.attachments?.map(a => a.file.name) ?? [])
        ].filter(Boolean);
        if (isBatch) {
          const val = info.getValue();
          return val
            ? <Text textAlign="center" fontSize="14px">{format(new Date(val), 'MM/dd/yyyy')}</Text>
            : <Text color="gray.400" fontSize="14px">N/A</Text>;
        }
        return <AttachmentCell attachments={attachmentNames} />;
      },
    }),

    // ── Col 4: The hospital name/location of the state associated ──────────────────────
    ...((authContext?.currentUser?.user_type === ClientType.HEALTH_SYSTEM || 
      authContext?.currentUser?.user_type === ClientType.PROVIDER) ? 
      [columnHelper.accessor('hospital', {
      id: 'location',
      header: () => 'Location',
      cell: info => {
        const isBatch = info.row.depth === 0;
        if (isBatch) {
          let uploaderDetails = '-';
          if (info.row.original.hospital) {
            uploaderDetails = `${info.row.original.hospital?.name} - ${info.row.original.hospital?.state_abbr}`;
          } else if (info.row.original.uploader?.health_system?.state_abbr) {
            uploaderDetails = info.row.original.uploader?.health_system?.state_abbr;
          }
         return <Text textAlign="center" fontSize="14px">{uploaderDetails}</Text>
         }
        // return <AttachmentCell attachments={attachmentNames} />;
      },
    })] : []),

    // ── Col 3: Completed Date ─────────────────────────────────────────────────
    columnHelper.accessor('completed_date', {
      id: 'completed_date',
      header: () => 'Completed Date',
      subHeader: {
        1: { label: 'Completed Date', ...CHART_SUBHEADER, sortable: true },
      },
      cell: info => {
        const row             = info.row.original;
        const isBatch         = info.row.depth === 0;
        const effectiveStatus = isBatch ? getBatchStatus(row.subRows) : row.status;
        return <CompletedDateCell completedDate={info.getValue()} status={effectiveStatus} />;
      },
    }),

    // ── Col 4: Status ─────────────────────────────────────────────────────────
    columnHelper.accessor('status', {
      id: 'status',
      header: () => 'Status',
      subHeader: {
        1: { label: 'Status', ...CHART_SUBHEADER, sortable: true },
      },
      cell: info => {
        const isBatch = info.row.depth === 0;
        return isBatch
          ? <BatchStatusIndicator subRows={info.row.original.subRows} />
          : <ChartStatusBadge status={info.getValue()} />;
      },
    }),

    // ── Col 5: No. of Charts (batch) / Specialty (chart) ─────────────────────
    columnHelper.accessor(row => row, {
      id: 'chart_count',
      header: () => 'Files',
      subHeader: {
        // Different label at depth 1 — "No. of Charts" becomes "Specialty"
        1: { label: 'Specialty', ...CHART_SUBHEADER, sortable: true },
      },
      // sortValue tells CQTreeTable how to extract a primitive for sub-header sorting
      // accessorFn returns the whole row object which is not directly sortable
      sortValue: row => row.specialty?.name ?? '',
      cell: info => {
        const row     = info.getValue();
        const isBatch = info.row.depth === 0;
        if (isBatch) {
          return row.charts != null
            ? <Text textAlign="center" fontSize="14px">{row.charts}</Text>
            : <Text color="gray.400" fontSize="14px">N/A</Text>;
        }
        if (!row.specialty)
          return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
        return row.specialty?.name
          ? <Text textAlign="center" fontSize="13px">{row.specialty.name}</Text>
          : <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
      },
    }),


    // ── Col 7: Providers ────────────────────────────────────────────────────
    columnHelper.accessor(row => row, {
      id: 'providers',
      header: () => 'Providers',
      enableSorting: false,
      subHeader: {
        1: { label: 'Providers', ...CHART_SUBHEADER },
      },
      cell: info => {
        const rowVal = info.getValue();
        const isBatch = info.row.depth === 0;
        if (isBatch) {
          return <AttachmentCell attachments={rowVal.batch_providers} />
        }
        return <AttachmentCell attachments={(rowVal.providers ?? []).map(chart_prov => chart_prov.name)} />
      }
    }),

    // ── Col 6: RI Score ───────────────────────────────────────────────────────
    columnHelper.accessor('cq_score', {
      id: 'cq_score',
      header: () => 'RI Score',
      subHeader: {
        1: { label: 'RI Score', ...CHART_SUBHEADER, sortable: true },
      },
      cell: info => {
        const val = info.getValue();
        if (val == 0) return <Text color="gray.400" fontSize="14px">0</Text>;
        return val != null
          ? <Text textAlign="center" fontSize="14px">{val}</Text>
          : <Text color="gray.400" fontSize="14px">N/A</Text>;
      },
    }),


    // ── Col 8: Action — no sub-header label needed ────────────────────────────
    columnHelper.accessor(row => row.upload_id, {
      id: 'action',
      header: () => '',
      enableSorting: false,
      // subHeader intentionally omitted → blank cell in sub-header row
      cell: info => {
        const cellValue = info.getValue();
        const isBatch = info.row.depth === 0;
        const status = info.row.original.status;
        const isCompleted = status === 'completed' || status === 'rebuttal';
        const canAttachAdditionalDocs =
          !isBatch && ATTACHABLE_CHART_STATUSES.includes(status);
        const batchId = info.getValue();
        
        return (
          <HStack justifyContent="start" spacing={2}>
            <Tooltip
              label={!isCompleted ? "Available once reviewed." : ""}
              isDisabled={isCompleted}
              placement="top"
              hasArrow
            >
              <Box>
                <PrimaryButton
                  bg={AppColors.secondary}
                  fontSize="14px"
                  fontWeight="normal"
                  size="sm"
                  borderRadius="3xl"
                  px={5}
                  isDisabled={!isCompleted}
                  _hover={{
                    transition: 'all .1s ease',
                    bgColor: AppColors.white,
                    color: AppColors.secondary,
                    outline: `2px solid ${AppColors.secondary}`
                  }}
                  onClick={() => {
                    if (!isCompleted) return;

                    const paginationSession = getPaginationSession(PAGE_NAME, TABLE_NAME);                        
                    setPaginationSession(PAGE_NAME, TABLE_NAME, {
                      ...paginationSession, 
                      ...('searchText' in filterParams && {search: filterParams.searchText}),
                      ...(filterParams.ordering && {ordering: filterParams.ordering})
                    }) 

                    if (isBatch) {
                      onBatchNavigation(
                        info.row.index,
                        info.row.original.upload_id
                      )
                      navigate(
                        ROUTES.VIEW_BATCH_AUDIT_SHEET.replace(':batchId', cellValue)
                      );
                    } else {
                      onChartNavigation(
                        info.row.getParentRow().index,
                        info.row.index,
                        info.row.getParentRow().original.upload_id,
                        info.row.original.id
                      )
                      const routeFormat = { uploadId: info.row.original.id };
                      const route = ROUTES.VIEW_AUDIT_SHEET;
                      navigate(replaceRoute(route, routeFormat));
                    }
                  }}
                >
                  {isBatch ? 'View Batch' : 'View File'}
                </PrimaryButton>
              </Box>
            </Tooltip>

            {!isBatch ? (
              <>
                {canAttachAdditionalDocs && (
                  <Tooltip label="Add additional documents" placement="top" hasArrow>
                    <IconButton
                      aria-label="Add additional documents"
                      icon={
                        <UploadIcon
                          disableTooltip
                          style={{ width: 18, height: 18 }}
                        />
                      }
                      size="sm"
                      minW="32px"
                      h="32px"
                      variant="outline"
                      borderColor={AppColors.secondary}
                      color={AppColors.secondary}
                      onClick={() => handleAddAdditionalDocs(info.row.original)}
                      _hover={{
                        bg: AppColors.secondary,
                        color: AppColors.white,
                      }}
                    />
                  </Tooltip>
                )}
                <AuditSheetFileViewer
                  auditData={info.row.original}
                  icon={<EyeAvatarIcon style={{ cursor: 'pointer'}} />}
                  chart_id={info.row.original.id}
                  onOpen={() => {
                    onChartNavigation(
                        info.row.getParentRow().index,
                        info.row.index,
                        info.row.getParentRow().original.upload_id,
                        info.row.original.id
                      )
                  }}
                  onClose={initializingNavState}
                />
              </>
            ) : (
            <NotesDrawer
            tabType={"ALL"}
            batchId={batchId}
            onOpenNotes={obj => onOpenNotes(obj, batchId, "ALL")}
            onOpen={() => {
              onBatchNavigation(
                info.row.index,
                info.row.original.upload_id
              )
            }}
            onClose={initializingNavState}
            style={{
                width: 35,
                height: 35,
                cursor: 'pointer',
                display: 'inline-block',
                transition: "0.3s",
                filter: "invert(1)"
              }}
              
              onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
            /> 
            )}
          </HStack>
        );
      }
    }),

  ], [isAllChartLoading]);

  function handlePreviousPage() {
    getAllCharts.loadPrevious(PAGE_NAME, TABLE_NAME);
  }

  function handleNextPage() {
    getAllCharts.loadNext(PAGE_NAME, TABLE_NAME);
  }

  function handleSizeChange(size) {
    getAllCharts.loadSize(PAGE_NAME, TABLE_NAME, size );
  }

  function handlePageChangeByNumber(pageNumber) {
    getAllCharts.loadPageByNumber(PAGE_NAME, TABLE_NAME, pageNumber);
  }

  return (
    <Box>
      <CQTreeTable
        ref={tableRef}
        data={allCharts}
        columns={columns}
        getSubRows={(row) => row.subRows}
        manualPagination={true}
        manualSortBy={false}
        isLoading={isAllChartLoading}
        {...createTreeTableProps(highlightedRowId)}
        {...paginationProps(getAllCharts)}
        onAsc={header => {
          setOrdering(header);
        }}
        onDesc={header => {
          setOrdering(`-${header}`);
        }}
        onSortReset={() => {
          setOrdering(undefined);
        }}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onSizeChange={handleSizeChange}
        onPageChangeByNumber={handlePageChangeByNumber}
        autoExpand={row => row.expand}
      />
      <AddAdditionalDocsModal
        isOpen={addDocsModal.isOpen}
        onClose={addDocsModal.onClose}
        chart={selectedChart}
        onUploadComplete={() => {
          addDocsModal.onClose();
          getAllCharts.loadAPI(PAGE_NAME, TABLE_NAME);
        }}
      />
    </Box>
  );
};

export default NewChartsTable;
